import { differenceInDays } from 'date-fns';
import {
  AlertCircle,
  ArrowLeft,
  Download,
  Loader2,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';

import { type GetRequest, NotFound } from '@cold-ambulance/client';
import {
  type TTStatus,
  type TimelineEvent,
  deriveTTDeadline,
  deriveTTStatus,
  deriveVendorTTStatus,
  getTTRejectionReason,
  getVendorTTRejectionReason,
} from '@cold-ambulance/isomorphic';
import {
  Alert,
  AlertDescription,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Skeleton,
} from '@cold-ambulance/shadcn';
import {
  EmptySection,
  ReviewRequestDetails,
  ReviewRequestDetailsSkeleton,
  TheButton,
  UploadField,
  client,
  formatDate,
  uploadFile,
  useAction,
  useData,
} from '@cold-ambulance/ui';

import { OfferCard, OfferCardSkeleton } from '../../components/offer-card.tsx';

export function RequestDetailsPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const requestQuery = useData(
    'GET /requests/{id}',
    { id: id! },
    { enabled: !!id },
  );

  return (
    <div className="page-container">
      <div className="mx-auto w-full max-w-3xl">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">{t('requests.requestDetails')}</h1>
          <Link
            to="/requests"
            viewTransition
            className="text-primary flex gap-x-1 text-sm font-bold"
          >
            <span>{t('common.back')}</span>
            <ArrowLeft />
          </Link>
        </div>
        <div className="mt-8 space-y-8">
          {requestQuery.isLoading || !requestQuery.data ? (
            <>
              <ReviewRequestDetailsSkeleton />
              <OffersSectionSkeleton />
            </>
          ) : (
            <>
              <ReviewRequestDetails request={requestQuery.data} />
              <TTUploadSection
                request={requestQuery.data}
                onSuccess={() => requestQuery.refetch()}
              />
              <OffersSection
                request={requestQuery.data}
                offers={requestQuery.data.offers.slice(0, 4)}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SingleTTUpload({
  label,
  reviewerLabel,
  ttFileId,
  ttStatus,
  rejectionReason,
  uploadEndpoint,
  deleteEndpoint,
  offerId,
  onSuccess,
}: {
  label: React.ReactNode;
  reviewerLabel: string;
  ttFileId: string | null;
  ttStatus: TTStatus;
  rejectionReason: string | null;
  uploadEndpoint:
    | 'POST /offers/{id}/tt'
    | 'POST /offers/{id}/vendor-tt';
  deleteEndpoint:
    | 'DELETE /offers/{id}/tt'
    | 'DELETE /offers/{id}/vendor-tt';
  offerId: string;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isVendorTt = uploadEndpoint === 'POST /offers/{id}/vendor-tt';
  const uploadMutation = useAction(uploadEndpoint, {
    meta: { toast: true },
    onSuccess: () =>
      toast.success(
        t(
          isVendorTt
            ? 'shared.actions.offer.vendorTtUploaded'
            : 'shared.actions.offer.customerTtUploaded',
        ),
      ),
  });
  const deleteMutation = useAction(deleteEndpoint, {
    meta: { toast: true },
    onSuccess: () =>
      toast.success(
        t(
          isVendorTt
            ? 'shared.actions.offer.vendorTtDeleted'
            : 'shared.actions.offer.customerTtDeleted',
        ),
      ),
  });

  if (ttStatus === 'APPROVED') {
    return null;
  }

  const handleFileChange = async (newFile: File | null) => {
    if (!newFile) {
      setFile(null);
      return;
    }

    setFile(newFile);
    setIsUploading(true);

    try {
      const fileId = await uploadFile(newFile);
      await uploadMutation.mutateAsync({ id: offerId, fileId });
      onSuccess();
    } catch (error) {
      console.error('Failed to upload TT:', error);
      setFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteMutation.mutateAsync({ id: offerId });
      setFile(null);
      onSuccess();
    } catch (error) {
      console.error('Failed to delete TT:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold">{label}</span>
        <span className="text-muted-foreground text-xs">{reviewerLabel}</span>
      </div>

      {rejectionReason && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>
            {t('requestDetails.ttRejected')} {rejectionReason}
          </AlertDescription>
        </Alert>
      )}

      {ttFileId ? (
        <div className="flex items-center justify-between rounded-md border bg-gray-50 p-4">
          <div className="flex items-center gap-2">
            <Download className="size-4 text-green-600" />
            <span className="text-sm font-medium">{t('requestDetails.ttUploaded')}</span>
            <span className="text-muted-foreground text-xs">
              {t('requestDetails.awaitingReview')}
            </span>
          </div>
          <TheButton
            variant="ghost"
            size="sm"
            onClick={handleDelete}
            loading={isDeleting}
            icon={<Trash2 className="size-4" />}
            className="text-destructive hover:text-destructive/90"
          >
            {t('common.delete')}
          </TheButton>
        </div>
      ) : (
        <UploadField
          name={`ttFile-${uploadEndpoint}`}
          value={file}
          onChange={handleFileChange}
          suffix={
            isUploading ? <Loader2 className="size-4 animate-spin" /> : null
          }
          canDelete={!isUploading}
        />
      )}
    </div>
  );
}

function MedHubIbanLabel() {
  const { t } = useTranslation();
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="text-primary cursor-pointer underline underline-offset-2"
        >
          {t('requestDetails.medhubTransfer')}
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('requestDetails.medhubBankInfo')}</DialogTitle>
          <DialogDescription>
            {t('requestDetails.transferToAccount')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm" dir="ltr">
          <div className="flex justify-between border-b pb-2">
            <span className="text-muted-foreground">Bank Name</span>
            <span className="font-medium">Al Rajhi Bank</span>
          </div>
          <div className="flex justify-between border-b pb-2">
            <span className="text-muted-foreground">Account Holder</span>
            <span className="font-medium">MedHub Platform</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">IBAN</span>
            <span className="font-medium">SA0000000000000000000000</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VendorBankCertLabel({ offerId }: { offerId: string }) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async () => {
    setIsLoading(true);
    try {
      const { signedUrl } = await client.request(
        'GET /offers/{id}/vendor-bank-certificate',
        { id: offerId },
      );
      window.open(signedUrl, '_blank', 'noopener');
    } catch (error) {
      if (error instanceof NotFound) {
        toast.error(t('requestDetails.vendorBankCertNotUploaded'));
      } else {
        toast.error(t('requestDetails.bankCertDownloadError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      type="button"
      className="text-primary cursor-pointer underline underline-offset-2 disabled:opacity-50"
      disabled={isLoading}
      onClick={handleClick}
    >
      {isLoading ? t('common.loading') : t('requestDetails.vendorTransfer')}
    </button>
  );
}

function TTUploadSection({
  request,
  onSuccess,
}: {
  request: GetRequest;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const acceptedOffer = request.offers.find((o) => o.id === request.offerId);

  if (!acceptedOffer || acceptedOffer.status !== 'ACCEPTED') {
    return null;
  }

  const timeline = acceptedOffer.timeline as TimelineEvent[];
  const caTTStatus = deriveTTStatus(timeline);
  const vendorTTStatus = deriveVendorTTStatus(timeline);
  const ttDeadline = deriveTTDeadline(timeline);
  const caTTRejectionReason = getTTRejectionReason(timeline);
  const vendorTTRejectionReason = getVendorTTRejectionReason(timeline);

  const daysRemaining = ttDeadline
    ? differenceInDays(ttDeadline, new Date())
    : null;

  if (caTTStatus === 'APPROVED' && vendorTTStatus === 'APPROVED') {
    return null;
  }

  return (
    <Card className="border-primary">
      <CardContent className="space-y-4 pt-6">
        <h3 className="text-lg font-bold">{t('requestDetails.ttUploadTitle')}</h3>

        {daysRemaining !== null && ttDeadline && (
          <Alert
            variant={daysRemaining <= 3 ? 'destructive' : 'default'}
            className="border-amber-200 bg-amber-50"
          >
            <AlertCircle className="size-4" />
            <AlertDescription>
              {t('requestDetails.deadline')} {formatDate(ttDeadline.toISOString())} ({t('requestDetails.remaining')}{' '}
              {daysRemaining} {daysRemaining === 1 ? t('requestDetails.day') : t('requestDetails.days')})
            </AlertDescription>
          </Alert>
        )}

        <SingleTTUpload
          label={<MedHubIbanLabel />}
          reviewerLabel={t('requestDetails.reviewedByAdmin')}
          ttFileId={acceptedOffer.ttFileId || null}
          ttStatus={caTTStatus}
          rejectionReason={caTTRejectionReason}
          uploadEndpoint="POST /offers/{id}/tt"
          deleteEndpoint="DELETE /offers/{id}/tt"
          offerId={acceptedOffer.id}
          onSuccess={onSuccess}
        />

        <SingleTTUpload
          label={
            <VendorBankCertLabel offerId={acceptedOffer.id} />
          }
          reviewerLabel={t('requestDetails.reviewedByVendor')}
          ttFileId={acceptedOffer.vendorTtFileId || null}
          ttStatus={vendorTTStatus}
          rejectionReason={vendorTTRejectionReason}
          uploadEndpoint="POST /offers/{id}/vendor-tt"
          deleteEndpoint="DELETE /offers/{id}/vendor-tt"
          offerId={acceptedOffer.id}
          onSuccess={onSuccess}
        />

      </CardContent>
    </Card>
  );
}

function OffersSection({
  request,
  offers,
}: {
  request: GetRequest;
  offers: GetRequest['offers'];
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      {offers.length > 0 ? (
        <>
          <div className="mb-7 flex w-full items-center justify-between">
            <p className="font-medium">{t('offers.submittedOffers')}</p>
            <Link
              to={`../${request.id}/offers`}
              className="text-primary flex gap-x-1 text-sm font-bold"
            >
              <span>{t('common.viewAll')}</span>
              <ArrowLeft />
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {offers.map((offer, index) => (
              <OfferCard
                key={offer.id}
                offer={offer}
                request={request}
                index={index + 1}
              />
            ))}
          </div>
        </>
      ) : (
        <EmptySection
          title={t('offers.noOffersTitle')}
          subtitle={t('offers.noOffers')}
          description={t('offers.noOffersReceived')}
        />
      )}
    </div>
  );
}

function OffersSectionSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex w-full items-center justify-between">
        <Skeleton className="h-4 w-28" />
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-5 w-5 rounded" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <OfferCardSkeleton />
        <OfferCardSkeleton />
        <OfferCardSkeleton />
        <OfferCardSkeleton />
      </div>
    </div>
  );
}
